import { Search } from 'lucide-react';

import type { RegionOption } from '../../../../shared/regionAccessOptions';
import { ROLE_DEFS, STATUS_DEFS, STATUS_FILTER_ORDER } from '../roleTaxonomy';
import type { UserRoleId, UserStatus } from '../roleTaxonomy';

import UsersRegionFilterControl from './UsersRegionFilterControl';

export interface UsersFilterState {
  search: string;
  role: UserRoleId | 'any';
  status: UserStatus | 'any';
  /** Region id, not code: a region may have no code, and codes aren't unique. */
  regionId: string | 'any';
  showInactive: boolean;
}

interface UsersFilterBarProps {
  filters: UsersFilterState;
  onChange: (next: UsersFilterState) => void;
  /** When set, the region control is a locked indicator (RD scope) instead of
   *  an open filter. */
  scopedRegionLabel?: string | null;
  /** All regions in the tenant — populates the admin region select. */
  regionOptions: RegionOption[];
}

const selectClass =
  'h-8 px-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-[#1a1f1c] text-[12.5px] text-bark-900 dark:text-[#e8ece9] cursor-pointer';

/** Search + role/region/status filters + show-inactive toggle. For an RD the
 *  region control is replaced by a locked "your region" indicator. */
export default function UsersFilterBar({
  filters,
  onChange,
  scopedRegionLabel = null,
  regionOptions,
}: UsersFilterBarProps) {
  return (
    <div className="flex items-center gap-2 py-2.5 pl-[18px] pr-3.5 border-b border-stone-200 dark:border-stone-800">
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500 dark:text-stone-400 pointer-events-none">
          <Search size={14} />
        </span>
        <input
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="Search by name, email, region…"
          className="w-[280px] h-8 pl-8 pr-2.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-[#1a1f1c] text-[13px] text-bark-900 dark:text-[#e8ece9] outline-none focus:ring-2 focus:ring-moss-500 focus:ring-offset-1"
        />
      </div>

      <select
        className={selectClass}
        value={filters.role}
        onChange={(e) => onChange({ ...filters, role: e.target.value as UserRoleId | 'any' })}
        aria-label="Filter by role"
      >
        <option value="any">Role: Any</option>
        {ROLE_DEFS.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>

      <UsersRegionFilterControl
        value={filters.regionId}
        onChange={(regionId) => onChange({ ...filters, regionId })}
        scopedRegionLabel={scopedRegionLabel}
        regionOptions={regionOptions}
        className={selectClass}
      />

      <select
        className={selectClass}
        value={filters.status}
        onChange={(e) => onChange({ ...filters, status: e.target.value as UserStatus | 'any' })}
        aria-label="Filter by status"
      >
        <option value="any">Status: Any</option>
        {STATUS_FILTER_ORDER.map((s) => (
          <option key={s} value={s}>
            {STATUS_DEFS[s].label}
          </option>
        ))}
      </select>

      <div className="flex-1" />

      <label className="inline-flex items-center gap-2 text-[12px] text-stone-600 dark:text-stone-400 cursor-pointer select-none">
        <input
          type="checkbox"
          className="accent-moss-600"
          checked={filters.showInactive}
          onChange={(e) => onChange({ ...filters, showInactive: e.target.checked })}
        />
        Show inactive
      </label>
    </div>
  );
}
